import React from 'react';
import ReactMarkdown from 'react-markdown';

const ResponseFormatter = ({ data }) => {
  if (!data) return null;

  // Processes links consistently across formats
  const processLinks = (text) => {
    if (!text) return '';
    return text.replace(
      /<a\s+(?:[^>]*?\s+)?href=(['"])([^'"]*)\1[^>]*>([\s\S]*?)<\/a>/g,
      (match, quote, url, content) => `[${content.trim()}](${url})`
    );
  };

  // Process all text fields that might contain links
  const processAllLinks = (obj) => {
    if (!obj) return obj;
    
    const result = { ...obj };
    for (const key in result) {
      if (typeof result[key] === 'string') {
        result[key] = processLinks(result[key]);
      } else if (Array.isArray(result[key])) {
        result[key] = result[key].map(item => 
          typeof item === 'string' ? processLinks(item) : processAllLinks(item)
        );
      } else if (typeof result[key] === 'object') {
        result[key] = processAllLinks(result[key]);
      }
    }
    return result;
  };

  // Plain text conversion for downloads
  const getPlainText = (markdownContent) => {
    return markdownContent
      .replace(/#{1,6} /g, '')                    // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1')           // Remove bold
      .replace(/\*(.*?)\*/g, '$1')               // Remove italics
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')      // Convert links to text
      .replace(/\n\s+\n/g, '\n\n')               // Clean whitespace
      .replace(/\n{3,}/g, '\n\n')                // Normalize line breaks
      .trim();
  };

  // Formats content based on type and structure
  const formatContent = (content) => {
    try {
      // Parse the response body if it's a string
      let processedContent = content;
      let messageLevel = content;

      if (content?.body && typeof content.body === 'string') {
        try {
          processedContent = JSON.parse(content.body);
        } catch (e) {
          console.error('Error parsing response body:', e);
        }
      }

      // Handle the response payload array
      if (processedContent?.payload && Array.isArray(processedContent.payload)) {
        processedContent = processedContent.payload[0];
      }

      // Handle response structure
      if (processedContent?.project_id === 'digital_horizon_adviser') {
        const { system, translated_phrases: tp } = processAllLinks(processedContent.content);
        
        // Check for response_format_b first
        if (system?.response_type === "response_format_b") {
          return `
${system?.original_question || 'N/A'}  
${system?.language ? `[${system.language}]` : ''}: ${system?.normed_question || 'N/A'}  
${tp?.response_header || 'Digital Horizon Course Recommendation'}  
${messageLevel.timestamp || 'N/A'}

${messageLevel.id || 'N/A'}

${tp?.response || ''}`;
        }

        // If not response_format_b, process as response_format_a
        const { translated_phrases, analysis, recommended_track, price_estimate } = processAllLinks(processedContent.content);
        
        return `
${messageLevel.timestamp || 'N/A'}

${system?.original_question || 'N/A'}  
${system?.language ? `[${system.language}]` : ''}: ${system?.normed_question || 'N/A'}  

${tp?.response_header || 'Digital Horizon Course Recommendation'}  
**${messageLevel.id || 'N/A'}**  
${tp?.disclaimer || 'Disclaimer'}

${recommended_track ? `# ${recommended_track.name || 'N/A'}

## ${tp?.core_problems || 'Core Problems Identified'}
${tp ? analysis.core_problems.map(problem => `- ${problem}`).join('\n') : ''}` : ''}

${analysis?.relevant_modules ? `## ${tp?.relevant_modules || 'Recommended Learning Modules'}
${analysis.relevant_modules.map(module =>
          `- **${module.name || 'Unnamed Module'}**  
  ${module.relevance || 'N/A'}  
  ${tp?.number_of_hours || ''}: ${module.hours || 'N/A'}`).join('\n\n')}` : ''}

${tp?.selected_modules_search || 'Recommended modules details'}

${recommended_track?.implementation_outcomes ? `## ${tp?.implementation_outcomes || 'Expected Learning Outcomes'}
${recommended_track.implementation_outcomes.map(outcome => `- ${outcome}`).join('\n')}` : ''}

${price_estimate ? `## ${tp?.price_estimate || 'Price Estimate'}*
${tp?.employees || 'Number of Employees'}: ${price_estimate.employees || 'N/A'}  
${tp?.hours_per_employee || 'Hours per Employee'}: ${price_estimate.hours_per_employee || 'N/A'}  
${tp?.total_hours || 'Total Training Hours'}: ${price_estimate.total_hours || 'N/A'}  
${tp?.course_cost_per_hour_czk || 'Course Cost per Hour'}: ${price_estimate.course_cost_per_hour_czk?.toLocaleString('cs-CZ') || 'N/A'} CZK  
**${tp?.total_course_cost_czk || 'Total Course Cost'}: ${price_estimate.total_course_cost_czk?.toLocaleString('cs-CZ') || 'N/A'} CZK**

*) ${tp?.contribution_npo_per_hour_czk || 'Government contribution to wage per hour'} ${price_estimate.contribution_npo_per_hour_czk?.toLocaleString('cs-CZ') || 'N/A'} CZK. ${tp?.additional_contribution_npo_czk || 'Total government contribution to wage'} ${price_estimate.additional_contribution_npo_czk?.toLocaleString('cs-CZ') || 'N/A'} CZK.

${tp?.npo_link || ''}` : ''}`;
      }

      if (typeof processedContent === 'object' && processedContent.type === 'message' && processedContent.content) {
        const messageText = processedContent.content[0]?.text;
        if (!messageText) {
          return 'Empty response';
        }

        // Extract response format from XML-style tags
        const match = /<response_format_[ab]>(.*?)<\/response_format_[ab]>/s.exec(messageText);
        if (match) {
          try {
            const jsonData = JSON.parse(match[1].trim());
            if (jsonData?.project_id === 'digital_horizon_adviser') {
              processedContent = jsonData;
              return formatContent(processedContent);
            }
          } catch (e) {
            console.error('Error parsing XML response:', e);
            return messageText;
          }
        }
        return messageText;
      }

      return String(processedContent);

    } catch (error) {
      console.error('Error in formatContent:', error);
      return String(content);
    }
  };

  const formattedContent = formatContent(data);

  // Expose methods for ResponseDisplay
  React.useEffect(() => {
    const formatter = document.querySelector('.response-formatter');
    if (formatter) {
      formatter.getFormattedContent = () => formattedContent;
      formatter.getPlainText = () => getPlainText(formattedContent);
      formatter.getMetadata = () => {
        // Use same parsing logic as formatContent
        let processedContent = data;
        let messageLevel = data;

        if (data?.body && typeof data.body === 'string') {
          try {
            processedContent = JSON.parse(data.body);
          } catch (e) {}
        }

        if (processedContent?.payload && Array.isArray(processedContent.payload)) {
          processedContent = processedContent.payload[0];
        }
        
        return {
          timestamp: messageLevel.timestamp || Math.floor(Date.now() / 1000),
          id: messageLevel.id || 'NO_ID'
        };
      };
    }
  }, [formattedContent, data]);

  return (
    <div className="prose prose-sm max-w-none bg-gray-50 p-6 rounded-lg response-formatter">
      <ReactMarkdown
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800" />
          )
        }}
      >
        {formattedContent}
      </ReactMarkdown>
    </div>
  );
};

export default ResponseFormatter;